import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { X, Link2, Loader2, Target, Unlink, Search } from "lucide-react";

const fmtEUR = (n) => n > 0 ? `${Number(n).toLocaleString("es-ES", { maximumFractionDigits: 0 })}€` : "—";

// Reglas de negocio v1 (sección 6, 12/10/2026): el backend ya calcula
// `match_tier` (destacada ≥90 / revisión secundaria 75-<90 / no_sugerir <75,
// con contradicción dura siempre no_sugerir) desde la clave versionada
// `links.similarity.*` — antes este color usaba sus propios umbrales fijos
// (66/33) sin relación con la regla de negocio real.
const TIER_STYLE = {
  destacada: "bg-emerald-100 text-emerald-700 border-emerald-200",
  revision_secundaria: "bg-amber-100 text-amber-700 border-amber-200",
  no_sugerir: "bg-gray-100 text-gray-500 border-gray-200",
};
const TIER_LABEL = {
  destacada: "sugerencia destacada",
  revision_secundaria: "revisión secundaria",
  no_sugerir: "no sugerido",
};
const scoreColor = (tier) => TIER_STYLE[tier] || TIER_STYLE.no_sugerir;

/**
 * Iter248 · Selector de candidatos Oferta TIC ↔ Tender360 (`db.licitaciones`).
 * Distinto del legacy `LinkLicitacionModal` (que vincula contra
 * `ofertas_comercial`): este busca contra la fuente canónica Tender360,
 * rankea por similitud (expediente/título/municipio/importe) y SIEMPRE
 * exige confirmación humana antes de vincular — nunca autovincula.
 */
export default function LinkTender360Modal({ open, offer, onClose, onLinked }) {
  const [items, setItems] = useState([]);
  const [offerLote, setOfferLote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(null);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const load = useCallback(async (query) => {
    if (!offer?.id) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/offers/${offer.id}/tender360-candidates`, { params: { limit: 15, q: query || "" } });
      setItems((data.items || []).map(it => ({ ...it, offer_lote: data.offer_lote })));
      setOfferLote(data.offer_lote || null);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.detail || "No se pudieron cargar las candidatas de Tender360");
    }
    finally { setLoading(false); }
  }, [offer?.id]);

  useEffect(() => { if (open && offer?.id) { setQ(""); } }, [open, offer?.id]);

  // Iter250 · Búsqueda instantánea (igual que el selector legacy): dispara
  // con debounce al teclear, sin bloquear el ranking por defecto al abrir.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => load(q), 300);
    return () => clearTimeout(t);
  }, [q, open, load]);

  const linkTo = async (lic) => {
    if (linking) return;
    let overrideReason = null;
    if (lic.lote_mismatch) {
      overrideReason = window.prompt(
        `⚠ ESTA CANDIDATA ES INCOMPATIBLE\n\nLa oferta es de "${lic.offer_lote || "—"}" y esta licitación es de "${lic.lote || "—"}" — son oportunidades DISTINTAS aunque compartan expediente.\n\nSi aun así quieres vincularlas como excepción, indica el motivo (mínimo 10 caracteres). Déjalo vacío para cancelar:`,
        ""
      );
      if (!overrideReason || overrideReason.trim().length < 10) return;
    }
    if (!window.confirm(
      `Vas a vincular esta oferta con la licitación de Tender360:\n\n"${lic.titulo}"${lic.municipio ? ` · ${lic.municipio}` : ""}${lic.expediente ? `\nExpediente: ${lic.expediente}${lic.lote ? ` · ${lic.lote}` : ""}` : ""}${overrideReason ? `\n\n⚠ Vínculo excepcional, motivo: "${overrideReason}"` : ""}\n\n¿Confirmas la vinculación?`
    )) return;
    setLinking(lic.id);
    try {
      const payload = { licitacion_id: lic.id };
      if (overrideReason) payload.override_reason = overrideReason.trim();
      const { data } = await axios.post(`${API}/offers/${offer.id}/link-tender360`, payload);
      onLinked?.(data);
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo vincular");
    } finally { setLinking(null); }
  };

  const unlink = async () => {
    if (!window.confirm("¿Desvincular esta oferta de la licitación Tender360?")) return;
    try {
      await axios.post(`${API}/offers/${offer.id}/unlink-tender360`);
      onLinked?.({ ok: true, linked_tender360_id: null });
      onClose?.();
    } catch (e) {
      alert(e?.response?.data?.detail || "No se pudo desvincular");
    }
  };

  if (!open) return null;
  const isLinked = !!offer?.linked_tender360_id;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="link-tender360-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="font-bold text-gray-900 flex items-center gap-2 text-lg font-['Outfit']">
              <Target size={18} className="text-emerald-600" /> Vincular con Tender360
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">
              {isLinked ? (
                <>Vinculada actualmente · <b className="text-emerald-700">{offer?.linked_tender360_state || "—"}</b></>
              ) : (
                <>Candidatas rankeadas por similitud (expediente, título, municipio, importe)</>
              )}
            </p>
          </div>
          <button onClick={onClose} data-testid="link-tender360-close-btn"><X size={18} className="text-gray-400" /></button>
        </div>

        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
            <p className="text-xs text-emerald-900">
              <b>Oferta:</b> {offer?.title} {offer?.municipality ? `· ${offer.municipality}` : ""}
              {offerLote && <span className="ml-2 text-[10px] font-bold bg-emerald-100 px-1.5 py-0.5 rounded">{offerLote} detectado</span>}
            </p>
          </div>

          <div className="relative">
            <Search size={14} className="absolute left-3 top-3 text-gray-400" />
            <input
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Buscar por título / expediente / municipio en Tender360…"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#007934]"
              data-testid="link-tender360-search-input"
            />
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-8"><Loader2 size={22} className="animate-spin text-gray-400" /></div>
          ) : error ? (
            <p className="text-center text-rose-500 text-sm py-8" data-testid="link-tender360-error">{error}</p>
          ) : items.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-8" data-testid="link-tender360-empty">
              {q ? "Sin resultados para esa búsqueda." : "No hay licitaciones en Tender360 todavía."}
            </p>
          ) : (
            <div className="space-y-2">
              {items.map(lic => {
                const isCurrent = lic.id === offer?.linked_tender360_id;
                return (
                  <div key={lic.id} className={`border rounded-lg p-3 flex items-start justify-between gap-3 ${isCurrent ? "border-emerald-300 bg-emerald-50/50" : lic.lote_mismatch ? "border-rose-200 bg-rose-50/40" : "border-gray-100 hover:bg-gray-50"}`}
                       data-testid={`link-tender360-candidate-${lic.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border whitespace-nowrap shrink-0 ${scoreColor(lic.match_tier)}`}
                              data-testid={`link-tender360-score-${lic.id}`} title={TIER_LABEL[lic.match_tier] || ""}>
                          {lic.match_score_pct}% · {TIER_LABEL[lic.match_tier] || "no sugerido"}
                        </span>
                        <p className="font-semibold text-sm text-gray-900 truncate">{lic.titulo || "—"}</p>
                        {lic.lote_mismatch && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-300 whitespace-nowrap shrink-0" data-testid={`link-tender360-lote-mismatch-${lic.id}`}>
                            ⚠ incompatible
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[11px] text-gray-500">
                        {lic.expediente && <span>Exp. <span className="font-mono text-gray-700">{lic.expediente}</span></span>}
                        {lic.lote && <span className="font-semibold text-gray-700">{lic.lote}</span>}
                        {lic.municipio && <span>{lic.municipio}</span>}
                        {lic.importe_licitacion > 0 && <span>{fmtEUR(lic.importe_licitacion)}</span>}
                        {lic.estado_crm && (
                          <span className="inline-block bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-semibold uppercase text-[9px]">
                            {lic.estado_crm}
                          </span>
                        )}
                      </div>
                      {lic.match_reasons?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`link-tender360-reasons-${lic.id}`}>
                          {lic.match_reasons.map((r, ri) => (
                            <span key={ri} className="text-[9px] bg-gray-50 border border-gray-200 text-gray-500 px-1.5 py-0.5 rounded-full">{r}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    {isCurrent ? (
                      <span className="text-[10px] font-semibold text-emerald-700 uppercase self-center">Actual</span>
                    ) : (
                      <button
                        onClick={() => linkTo(lic)}
                        disabled={!!linking}
                        className="bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#006528] disabled:opacity-60 flex items-center gap-1"
                        data-testid={`link-tender360-select-${lic.id}`}
                      >
                        {linking === lic.id ? <Loader2 size={11} className="animate-spin" /> : <Link2 size={11} />}
                        Vincular
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t bg-gray-50 flex items-center justify-between">
          {isLinked ? (
            <button onClick={unlink} className="text-xs text-rose-600 hover:text-rose-700 font-semibold flex items-center gap-1" data-testid="link-tender360-unlink-btn">
              <Unlink size={12} /> Desvincular
            </button>
          ) : <span />}
          <button onClick={onClose} className="border border-gray-200 text-gray-700 rounded-lg px-3 py-1.5 text-sm hover:bg-gray-100">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
